import { useEffect, useMemo, useRef, useState } from 'react'
import GlassOverlay from '../GlassOverlay'
import { exchangeRatesService, productService, placesService } from '../../services/api'
import { describeIdentifyImageError } from '../../services/identifyImageError'
import { readBarcodeFromBlob } from '../../services/scan/barcodeReader'
import { cropPreparedImage, prepareScanImage, revokePreview } from '../../services/scan/imagePrepare'
import { requestCurrentPosition } from '../../services/scan/geolocation'
import { createClientMutationId } from '../../utils/clientMutationId'
import { resolveJpyEurRate } from '../../services/scan/exchangeRateCache'
import { applyConversionToPayload } from '../../services/scan/scanSyncPolicy'
import { buildItemMeta, certaintyFromConfidence, persistImageUrl, storageKeyFromRef } from '../../utils/itemMeta'
import { isUsableProductName } from '../../utils/productName'
import ScanAnalyzeSheet from './ScanAnalyzeSheet'
import ScanCapture from './ScanCapture'
import ScanReview from './ScanReview'
import './ScanProductFlow.css'

function isAnalyzeCanceled(error) {
  return error?.code === 'ERR_CANCELED'
    || error?.name === 'CanceledError'
    || error?.name === 'AbortError'
}

const EMPTY_DRAFT = {
  name: '',
  originalName: '',
  translatedName: '',
  brand: '',
  model: '',
  variant: '',
  category: '',
  barcode: '',
  price: '',
  priceCandidates: [],
  currency: 'JPY',
  captureImageUrl: '',
  captureImageKey: '',
  captureUploadStatus: '',
  clientMutationId: '',
  taxStatus: '',
  convertedPrice: null,
  quantity: '1',
  observedAt: '',
  alternatives: [],
  previewUrl: '',
  additionalPreviews: [],
  officialImage: '',
  officialImages: [],
  officialImageMeta: null,
  generation: '',
  color: '',
  nameEn: '',
  analysisId: '',
  searchQueries: [],
  imageSearchPending: false,
  imageSearchError: '',
  failedOfficialImages: [],
  productBox: null,
  cropPreviewUrl: '',
  identificationStatus: '',
  pageTitle: '',
  needsDetailPhoto: false,
  missingOfficialImage: false,
  commercialRef: '',
  reverseSearchProvider: '',
  link: '',
  source: '',
  confidence: 0,
  exchangeRate: null,
  exchangeRateDate: '',
}

function toLocalInput(value) {
  const date = value ? new Date(value) : new Date()
  if (Number.isNaN(date.getTime())) return ''
  const pad = (n) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function parsePrice(value) {
  return Number(String(value ?? '').replace(/[¥€円,\s]/g, '').replace(',', '.'))
}

export default function ScanProductFlow({
  isOpen,
  onClose,
  onConfirm,
  onQueueOffline,
  onManual,
  submitting,
  submitError,
  returnFocusRef,
}) {
  const [step, setStep] = useState('capture')
  const [statusText, setStatusText] = useState('')
  const [analyzeError, setAnalyzeError] = useState('')
  const [draft, setDraft] = useState(EMPTY_DRAFT)
  const [photoBlob, setPhotoBlob] = useState(null)
  const [additionalPhotos, setAdditionalPhotos] = useState([])
  const [captureKind, setCaptureKind] = useState('product')
  const [searchingOfficial, setSearchingOfficial] = useState(false)
  const [recroping, setRecroping] = useState(false)
  const [analyzePhase, setAnalyzePhase] = useState('analyse')
  const submittedRef = useRef(false)
  const analyzeAbortRef = useRef(null)
  const imageAbortRef = useRef(null)
  const userEditedRef = useRef(false)
  const officialPickedRef = useRef(false)
  const cropEditedRef = useRef(false)
  const recropingRef = useRef(false)
  const pendingAnalyzeReset = useRef(false)
  const [store, setStore] = useState({
    status: 'idle',
    places: [],
    selected: null,
    storeName: '',
    storeAddress: '',
    query: '',
    error: '',
    coords: null,
    mode: '',
  })

  const abortAnalyze = () => {
    analyzeAbortRef.current?.abort()
    imageAbortRef.current?.abort()
  }

  const reset = () => {
    abortAnalyze()
    revokePreview(draft.previewUrl)
    revokePreview(draft.cropPreviewUrl)
    draft.additionalPreviews.forEach((preview) => revokePreview(preview.url))
    setStep('capture')
    setStatusText('')
    setAnalyzePhase('analyse')
    setAnalyzeError('')
    setDraft(EMPTY_DRAFT)
    setPhotoBlob(null)
    setAdditionalPhotos([])
    setCaptureKind('product')
    setSearchingOfficial(false)
    userEditedRef.current = false
    officialPickedRef.current = false
    cropEditedRef.current = false
    recropingRef.current = false
    setRecroping(false)
    setStore({
      status: 'idle',
      places: [],
      selected: null,
      storeName: '',
      storeAddress: '',
      query: '',
      error: '',
      coords: null,
      mode: '',
    })
  }

  useEffect(() => {
    if (isOpen) {
      if (pendingAnalyzeReset.current) {
        pendingAnalyzeReset.current = false
        reset()
      }
      return
    }
    abortAnalyze()
    if (submittedRef.current) {
      submittedRef.current = false
      reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  useEffect(() => {
    if (submitError) submittedRef.current = false
  }, [submitError])

  const mergeAnalysis = (analysis, barcodeValue = '') => setDraft((prev) => {
    const detectedPrice = analysis.price ?? analysis.observedPrice ?? ''
    const rawPrices = analysis.priceCandidates || analysis.price_candidates
    const priceCandidates = Array.isArray(rawPrices) ? rawPrices : (detectedPrice ? [detectedPrice] : prev.priceCandidates)
    const rawAlternatives = analysis.alternatives || analysis.suggestions
    const alternatives = Array.isArray(rawAlternatives) ? rawAlternatives.slice(0, 3) : prev.alternatives
    const officialMeta = analysis.officialImage && typeof analysis.officialImage === 'object'
      ? analysis.officialImage
      : (analysis.official_image_url ? {
        url: analysis.official_image_url,
        source_url: analysis.image_source_url || '',
        source_name: analysis.image_source_name || '',
      } : null)
    const officialImages = Array.isArray(analysis.official_images)
      ? analysis.official_images.filter((row) => row?.source_kind !== 'marketplace')
      : (analysis.official_image_url && !analysis.missing_official_image
        ? [{ url: analysis.official_image_url, source_kind: 'official' }]
        : prev.officialImages)
    const rawName = analysis.name_fr || analysis.translatedName || analysis.translated_name || analysis.suggestedName || ''
    const nameFr = isUsableProductName(rawName) ? rawName : (isUsableProductName(prev.name) ? prev.name : '')
    const chosenPrice = priceCandidates.length === 1
      ? priceCandidates[0]
      : priceCandidates.find((candidate) => candidate?.taxIncluded === true)
    const inferredTax = chosenPrice?.taxIncluded === true
      ? 'included'
      : chosenPrice?.taxIncluded === false
        ? 'excluded'
        : ''
    const locked = userEditedRef.current
    return {
      ...prev,
      name: locked ? prev.name : nameFr,
      translatedName: locked ? prev.translatedName : nameFr,
      originalName: analysis.ocr_raw || analysis.extractedText || prev.originalName,
      brand: locked ? prev.brand : (analysis.brand || prev.brand),
      model: locked ? prev.model : (analysis.model_fr || analysis.model || prev.model),
      variant: locked ? prev.variant : (analysis.variant_fr || analysis.variant || prev.variant),
      category: locked ? prev.category : (analysis.category_fr || analysis.category || prev.category),
      generation: analysis.generation || prev.generation,
      color: analysis.color || prev.color,
      nameEn: analysis.nameEn || analysis.name_en || analysis.canonical_name_international || prev.nameEn,
      analysisId: analysis.analysis_id || prev.analysisId,
      searchQueries: analysis.search_queries || analysis.searchQueries || prev.searchQueries,
      imageSearchPending: Boolean(analysis.image_search_pending),
      productBox: cropEditedRef.current ? prev.productBox : (analysis.product_box || prev.productBox),
      identificationStatus: analysis.identification_status || prev.identificationStatus,
      nameSource: analysis.name_source || prev.nameSource,
      cropSource: analysis.crop_source || prev.cropSource,
      detectedBox: cropEditedRef.current ? prev.detectedBox : (analysis.detected_box || prev.detectedBox),
      sourceTitles: analysis.source_titles || prev.sourceTitles,
      commercialRef: analysis.commercial_ref || prev.commercialRef,
      reverseSearchProvider: analysis.reverse_search_provider || prev.reverseSearchProvider,
      reverseSearchError: analysis.reverse_search_error || prev.reverseSearchError,
      barcode: analysis.barcode || barcodeValue || prev.barcode,
      alternatives,
      officialImage: officialPickedRef.current
        ? prev.officialImage
        : (analysis.missing_official_image
          ? ''
          : (analysis.official_image_url
            || officialMeta?.url
            || (typeof analysis.officialImage === 'string' ? analysis.officialImage : '')
            || prev.officialImage)),
      officialImages,
      pageTitle: analysis.page_title || prev.pageTitle,
      needsDetailPhoto: Boolean(analysis.needs_detail_photo),
      missingOfficialImage: Boolean(analysis.missing_official_image),
      officialImageMeta: officialPickedRef.current ? prev.officialImageMeta : (officialMeta || prev.officialImageMeta),
      imageSearchError: analysis.image_search_error || '',
      link: analysis.link || prev.link,
      source: analysis.source || prev.source,
      confidence: analysis.confidence ?? prev.confidence,
      price: detectedPrice ? String(detectedPrice) : prev.price,
      priceCandidates,
      currency: prev.currency || 'JPY',
      taxStatus: analysis.taxStatus || analysis.tax_status || inferredTax || prev.taxStatus,
      convertedPrice: analysis.convertedPriceEur
        ?? analysis.converted_price_eur
        ?? analysis.conversion?.eur
        ?? prev.convertedPrice,
    }
  })

  const identifyFromBlob = async (blob, barcodeValue, supplemental, cropBox, signal) => {
    const analysis = await productService.identifyImage(
      blob,
      barcodeValue || draft.barcode || '',
      supplemental,
      {
        country: 'JP',
        captureKind,
        crop_box: cropBox && cropBox.width ? cropBox : undefined,
      },
      { signal },
    )
    if (signal?.aborted) return null
    if (analysis?.product_box?.width > 0 && !cropEditedRef.current) {
      try {
        const cropped = await cropPreparedImage(blob, analysis.product_box)
        if (!signal?.aborted && !cropEditedRef.current) {
          setDraft((prev) => {
            revokePreview(prev.cropPreviewUrl)
            return {
              ...prev,
              cropPreviewUrl: cropped.previewUrl,
              productBox: analysis.product_box,
            }
          })
        }
      } catch {
        /* recadrage local facultatif */
      }
    }
    mergeAnalysis(analysis, barcodeValue || '')
    return analysis
  }

  const handleCapture = async (blob) => {
    abortAnalyze()
    const controller = new AbortController()
    analyzeAbortRef.current = controller
    const { signal } = controller
    setStep('analyze')
    setAnalyzeError('')
    setAnalyzePhase('analyse')
    setStatusText('Analyse de la photographie…')
    try {
      const prepared = await prepareScanImage(blob)
      if (signal.aborted) return
      if (captureKind === 'product') {
        setPhotoBlob(prepared.blob)
        cropEditedRef.current = false
        setDraft((prev) => {
          revokePreview(prev.previewUrl)
          revokePreview(prev.cropPreviewUrl)
          return {
            ...prev,
            previewUrl: prepared.previewUrl,
            cropPreviewUrl: '',
            productBox: null,
            observedAt: toLocalInput(),
            captureUploadStatus: 'uploading',
          }
        })
        void persistCapture(prepared.blob)
      } else {
        setAdditionalPhotos((prev) => [...prev, { kind: captureKind, blob: prepared.blob }])
        setDraft((prev) => ({
          ...prev,
          additionalPreviews: [...prev.additionalPreviews, { kind: captureKind, url: prepared.previewUrl }],
        }))
      }
      setAnalyzePhase('identification')
      setStatusText('Identification du produit…')
      const barcode = await readBarcodeFromBlob(prepared.blob)
      if (signal.aborted) return
      setStatusText('Recherche des informations…')
      const supplemental = captureKind === 'product' ? null : prepared.blob
      const primary = captureKind === 'product' ? prepared.blob : (photoBlob || prepared.blob)
      const analysis = await identifyFromBlob(
        primary,
        barcode?.value || draft.barcode || '',
        supplemental,
        captureKind === 'product' ? undefined : draft.productBox,
        signal,
      )
      if (signal.aborted || !analysis) return
      setAnalyzePhase('preparation')
      setStatusText('Préparation de la suggestion…')
      if (analysis.reverse_search_error === 'quota_exhausted') {
        setAnalyzeError('Quota SerpAPI épuisé. La recherche inversée est indisponible.')
      } else if (analysis.reverse_search_error === 'invalid_key') {
        setAnalyzeError('Clé SerpAPI refusée. Vérifiez la configuration.')
      } else if (!analysis.name_fr && !analysis.suggestedName && !analysis.translatedName && !draft.name) {
        setAnalyzeError('Aucune suggestion automatique. Vous pouvez saisir le nom et le prix.')
      }
      setCaptureKind('product')
      setStep('review')
      const persistedOfficial = (analysis.official_images || []).some((row) => (
        row?.url && row.storage === 'camit'
      ))
      const provisional = analysis.identification_status === 'provisional'
      if (!persistedOfficial && !provisional && !officialPickedRef.current) {
        void searchOfficialImage({
          ...analysis,
          name_fr: analysis.name_fr || analysis.suggestedName || analysis.translatedName || '',
          barcode: analysis.barcode || barcode?.value || '',
        })
      }
    } catch (error) {
      if (signal.aborted || isAnalyzeCanceled(error)) return
      setAnalyzeError(describeIdentifyImageError(error))
      setCaptureKind('product')
      setStep('review')
    }
  }

  const patchDraft = (partial) => {
    if (['name', 'translatedName', 'brand', 'model', 'variant', 'category'].some((key) => (
      Object.prototype.hasOwnProperty.call(partial, key)
    ))) {
      userEditedRef.current = true
    }
    if (partial.officialImage) officialPickedRef.current = true
    setDraft((prev) => ({ ...prev, ...partial }))
  }

  const handleRecrop = async (box) => {
    if (!photoBlob || recropingRef.current) return
    recropingRef.current = true
    cropEditedRef.current = true
    setRecroping(true)
    setDraft((prev) => ({ ...prev, productBox: box }))
    try {
      const cropped = await cropPreparedImage(photoBlob, box)
      setDraft((prev) => {
        revokePreview(prev.cropPreviewUrl)
        return { ...prev, cropPreviewUrl: cropped.previewUrl, productBox: box }
      })
      if (userEditedRef.current) return
      abortAnalyze()
      const controller = new AbortController()
      analyzeAbortRef.current = controller
      setStep('analyze')
      setAnalyzeError('')
      setAnalyzePhase('identification')
      setStatusText('Nouvelle analyse sur le recadrage…')
      const analysis = await identifyFromBlob(photoBlob, draft.barcode, null, box, controller.signal)
      if (controller.signal.aborted || !analysis) return
      setStep('review')
      if (
        analysis.identification_status !== 'provisional'
        && !officialPickedRef.current
        && (analysis.missing_official_image || !analysis.official_image_url)
      ) {
        void searchOfficialImage(analysis)
      }
    } catch (error) {
      if (!isAnalyzeCanceled(error)) {
        setAnalyzeError(describeIdentifyImageError(error))
      }
      setStep('review')
    } finally {
      recropingRef.current = false
      setRecroping(false)
    }
  }

  const handleOfficialDisplayError = (url) => {
    if (!url) return
    setDraft((prev) => {
      const captureUrl = persistImageUrl(prev.captureImageUrl)
      if (url === captureUrl || url === prev.previewUrl) return prev
      const failed = [...new Set([...(prev.failedOfficialImages || []), url])]
      const remaining = (prev.officialImages || []).filter((item) => item.url !== url)
      const nextOfficial = officialPickedRef.current
        ? (prev.officialImage === url ? (remaining[0]?.url || '') : prev.officialImage)
        : (remaining[0]?.url || '')
      return {
        ...prev,
        failedOfficialImages: failed,
        officialImages: remaining,
        officialImage: nextOfficial,
        imageSearchError: remaining.length ? prev.imageSearchError : (prev.imageSearchError || 'display_failed'),
        captureImageUrl: prev.captureImageUrl,
      }
    })
  }

  const applyOfficialImages = (images, extra = {}) => {
    const rows = Array.isArray(images?.official_images) ? images.official_images : []
    const officialRow = rows.find((row) => row?.source_kind === 'official')
    const first = images?.official_image_url || officialRow?.url || ''
    setDraft((prev) => ({
      ...prev,
      officialImage: officialPickedRef.current ? prev.officialImage : (first || prev.officialImage),
      officialImages: rows.length
        ? [
          ...rows,
          ...prev.officialImages.filter((item) => !rows.some((row) => row.url === item.url)),
        ]
        : prev.officialImages,
      officialImageMeta: officialPickedRef.current || !first ? prev.officialImageMeta : {
        url: first,
        key: String(images?.official_image_key || officialRow?.key || rows[0]?.key || '').trim(),
        source_url: images?.image_source_url || rows[0]?.source_url || '',
        source_name: images?.image_source_name || rows[0]?.source_name || '',
      },
      imageSearchError: images?.image_search_error || '',
      ...extra,
    }))
  }

  const searchOfficialImage = async (seed = null) => {
    imageAbortRef.current?.abort()
    const controller = new AbortController()
    imageAbortRef.current = controller
    setSearchingOfficial(true)
    setAnalyzeError('')
    setDraft((prev) => ({ ...prev, imageSearchPending: true, imageSearchError: '', failedOfficialImages: [] }))
    try {
      const source = seed || draft
      const status = source.identification_status || source.identificationStatus || draft.identificationStatus
      if (status === 'provisional' && !source.brand && !(source.nameEn || source.name_en)) {
        setDraft((prev) => ({ ...prev, imageSearchPending: false }))
        setSearchingOfficial(false)
        return
      }
      const found = await productService.searchOfficialImages({
        name_fr: source.name_fr || source.name || draft.name,
        name_en: source.nameEn || source.name_en || source.canonical_name_international || draft.nameEn,
        brand: source.brand || draft.brand,
        model_fr: source.model_fr || source.model || draft.model,
        variant_fr: source.variant_fr || source.variant || draft.variant,
        category_fr: source.category_fr || source.category || draft.category,
        generation: source.generation || draft.generation,
        color: source.color || draft.color,
        barcode: source.barcode || draft.barcode,
        commercial_ref: source.commercial_ref || source.commercialRef || draft.commercialRef,
        analysis_id: source.analysis_id || draft.analysisId,
        search_queries: source.search_queries || source.searchQueries || draft.searchQueries,
        identification_confidence: source.identification_confidence ?? source.confidence ?? draft.confidence,
        identification_status: source.identification_status || source.identificationStatus || draft.identificationStatus,
        link: source.link || draft.link,
      }, { signal: controller.signal })
      if (controller.signal.aborted) return
      const foundRows = Array.isArray(found?.official_images) ? found.official_images : []
      const foundUrl = found?.official_image_url || foundRows[0]?.url || ''
      applyOfficialImages(found, {
        imageSearchPending: false,
        missingOfficialImage: !foundUrl,
        imageSearchError: found?.image_search_error || (foundUrl ? '' : 'not_found'),
      })
      if (!foundUrl && !['download_failed', 'display_failed', 'search_unavailable'].includes(found?.image_search_error || '')) {
        setAnalyzeError('Aucune correspondance visuelle identifiée')
      }
    } catch (error) {
      if (controller.signal.aborted || isAnalyzeCanceled(error)) return
      setDraft((prev) => ({ ...prev, imageSearchPending: false, imageSearchError: 'search_unavailable' }))
      setAnalyzeError(describeIdentifyImageError(error) || 'La recherche d’image est indisponible.')
    } finally {
      if (!controller.signal.aborted) setSearchingOfficial(false)
    }
  }

  const importOfficialImage = async (file) => {
    try {
      const uploaded = await productService.uploadScanImage(file, file.name || 'official.jpg')
      if (!uploaded?.url) throw new Error('Le stockage n’a pas confirmé l’enregistrement.')
      applyOfficialImages({
        official_image_url: uploaded.url,
        official_image_key: uploaded.key || '',
        official_images: [{ url: uploaded.url, key: uploaded.key || '', source_name: 'import', source_kind: 'commerce' }],
        image_source_name: 'import',
      })
    } catch (error) {
      setAnalyzeError(describeIdentifyImageError(error))
    }
  }

  const assignOfficialImageUrl = async (url) => {
    try {
      const stored = await productService.cacheOfficialImage(url)
      const resolved = stored?.url || url
      applyOfficialImages({
        official_image_url: resolved,
        official_image_key: stored?.key || '',
        official_images: [{ url: resolved, key: stored?.key || '', source_url: stored?.source_url || url, source_name: stored?.source_domain || 'url' }],
        image_source_url: stored?.source_url || url,
        image_source_name: stored?.source_domain || 'url',
      })
    } catch (error) {
      setAnalyzeError(describeIdentifyImageError(error) || 'URL d’image invalide.')
    }
  }

  const askLocation = async (searchQuery = store.query) => {
    setStore((prev) => ({ ...prev, status: 'locating', error: '' }))
    try {
      const position = await requestCurrentPosition()
      const coords = {
        lat: position.lat,
        lng: position.lng,
        accuracy: position.accuracy,
        recordedAt: new Date().toISOString(),
      }
      const nearby = await placesService.nearby(coords.lat, coords.lng, searchQuery).catch(() => null)
      const reversed = await placesService.reverse(coords.lat, coords.lng).catch(() => null)
      const places = Array.isArray(nearby?.places) ? nearby.places : []
      const reverseAddress = String(reversed?.address || reversed?.display_name || '').trim()
      setStore((prev) => ({
        ...prev,
        status: 'ready',
        places,
        selected: null,
        storeName: prev.storeName,
        storeAddress: prev.storeAddress || reverseAddress,
        query: searchQuery,
        error: '',
        coords,
        mode: '',
      }))
    } catch (error) {
      const denied = String(error?.message || '').includes('denied')
      setStore((prev) => ({
        ...prev,
        status: denied ? 'denied' : 'error',
        error: denied
          ? 'Localisation refusée. Saisie manuelle disponible.'
          : 'Position introuvable. Saisie manuelle disponible.',
        mode: 'manual',
      }))
    }
  }

  const savePositionOnly = async () => {
    setStore((prev) => ({ ...prev, status: 'locating', error: '' }))
    try {
      const position = await requestCurrentPosition()
      const coords = {
        lat: position.lat,
        lng: position.lng,
        accuracy: position.accuracy,
        recordedAt: position.recordedAt || new Date().toISOString(),
      }
      setStore((prev) => ({
        ...prev,
        status: 'coords-only',
        mode: 'coords-only',
        coords,
        selected: null,
        error: '',
      }))
    } catch (error) {
      const denied = String(error?.message || '').includes('denied')
      setStore((prev) => ({
        ...prev,
        status: denied ? 'denied' : 'error',
        error: denied
          ? 'Localisation refusée. Saisie manuelle disponible.'
          : 'Position introuvable. Saisie manuelle disponible.',
        mode: 'manual',
      }))
    }
  }

  const searchPlaces = async (searchQuery = store.query) => {
    if (store.coords) {
      setStore((prev) => ({ ...prev, status: 'locating', error: '' }))
      const nearby = await placesService.nearby(store.coords.lat, store.coords.lng, searchQuery).catch(() => null)
      setStore((prev) => ({
        ...prev,
        status: 'ready',
        places: Array.isArray(nearby?.places) ? nearby.places : [],
        query: searchQuery,
      }))
      return
    }
    await askLocation(searchQuery)
  }

  const builtStore = useMemo(() => {
    if (['ignored', 'idle', 'locating'].includes(store.status)) return null
    const selected = store.selected
    const explicitCoords = store.status === 'coords-only'
    const manual = Boolean(store.storeName || store.storeAddress)
    if (!selected && !explicitCoords && !manual) return null
    const lat = selected?.lat ?? store.coords?.lat ?? null
    const lng = selected?.lng ?? store.coords?.lng ?? null
    const hasCoords = lat != null && lng != null
    return {
      name: store.storeName || selected?.name || '',
      address: store.storeAddress || selected?.address || '',
      lat,
      lng,
      accuracy: selected?.accuracy ?? store.coords?.accuracy ?? null,
      placeId: selected?.placeId || '',
      provider: selected?.provider || (hasCoords ? 'gps' : ''),
      recordedAt: store.coords?.recordedAt || '',
      mode: selected ? 'confirmed' : (hasCoords ? 'coords-only' : 'manual'),
    }
  }, [store])

  const persistCapture = async (blob) => {
    try {
      const uploaded = await productService.uploadScanImage(blob)
      const url = persistImageUrl(uploaded?.url)
      if (!url) {
        setDraft((prev) => ({ ...prev, captureUploadStatus: 'failed' }))
        return
      }
      setDraft((prev) => ({
        ...prev,
        captureImageUrl: url,
        captureImageKey: uploaded.key || '',
        captureUploadStatus: 'saved',
      }))
    } catch {
      setDraft((prev) => ({ ...prev, captureUploadStatus: 'failed' }))
    }
  }

  const submit = async () => {
    if (submittedRef.current || submitting) return
    const name = draft.name.trim()
    const price = parsePrice(draft.price)
    const quantity = Number.parseInt(draft.quantity, 10) || 1
    const currency = draft.currency === 'EUR' ? 'EUR' : 'JPY'
    if (!name) {
      setAnalyzeError('Indiquez un nom de produit.')
      return
    }
    if (!price || price < 0.01 || (currency !== 'JPY' && currency !== 'EUR')) {
      setAnalyzeError('Indiquez le prix constaté. Il n’est pas inventé automatiquement.')
      return
    }
    submittedRef.current = true
    const clientMutationId = draft.clientMutationId || createClientMutationId()
    patchDraft({ clientMutationId })
    const observedAt = draft.observedAt ? new Date(draft.observedAt).toISOString() : new Date().toISOString()
    const conversion = currency === 'EUR'
      ? null
      : await resolveJpyEurRate(() => exchangeRatesService.jpyToEur())
    const convertedPrice = currency === 'EUR'
      ? price
      : (conversion?.rate ? Math.round(price * Number(conversion.rate) * 100) / 100 : 0)
    const conversionStatus = currency === 'EUR'
      ? 'direct'
      : (convertedPrice ? (conversion.stale ? 'cached' : 'live') : 'pending')
    let officialUrl = persistImageUrl(draft.officialImage)
    let officialKey = String(
      draft.officialImageMeta?.key
      || storageKeyFromRef(draft.officialImage)
      || storageKeyFromRef(draft.officialImageMeta?.url)
      || '',
    ).trim()
    if (officialUrl && !/camit|supabase|product-images/i.test(officialUrl)) {
      try {
        const stored = await productService.cacheOfficialImage(officialUrl)
        officialUrl = persistImageUrl(stored?.url) || officialUrl
        officialKey = String(stored?.key || officialKey).trim()
        if (stored?.url || officialKey) {
          applyOfficialImages({
            official_image_url: officialUrl,
            official_images: [{
              url: officialUrl,
              key: officialKey,
              source_url: stored.source_url || draft.officialImageMeta?.source_url || officialUrl,
              source_name: stored.source_domain || draft.officialImageMeta?.source_name || '',
            }],
            image_source_url: stored.source_url || '',
            image_source_name: stored.source_domain || '',
          })
        }
      } catch {
        officialUrl = persistImageUrl(draft.officialImage)
      }
    }
    const createPayload = ({ captureUrl = '', captureKey = '', additionalUrls = [] } = {}) => {
      const persistentCapture = persistImageUrl(captureUrl)
      const raw = {
        name,
        link: draft.link || '',
        price: convertedPrice || 0,
        quantity,
        image_url: officialUrl,
        image_key: captureKey,
        certainty_label: certaintyFromConfidence(draft.confidence),
        client_mutation_id: clientMutationId,
        item_meta: buildItemMeta({
          barcode: draft.barcode,
          brand: draft.brand,
          category: draft.category,
          source: draft.source,
          confidence: draft.confidence,
          observedAt,
          store: builtStore,
          originalName: draft.originalName,
          translatedName: draft.translatedName || name,
          model: draft.model,
          variant: draft.variant,
          observedPrice: price,
          convertedPrice: convertedPrice || null,
          exchangeRate: currency === 'EUR' ? null : (conversion?.rate || null),
          exchangeRateDate: currency === 'EUR' ? '' : (conversion?.date || conversion?.retrieved_at || ''),
          conversionStatus,
          conversionStale: currency === 'EUR' ? false : Boolean(conversion?.stale),
          currency,
          convertedCurrency: 'EUR',
          tax: {
            status: draft.taxStatus,
            included: draft.taxStatus === 'included' ? true : draft.taxStatus === 'excluded' ? false : null,
          },
          images: {
            official: officialUrl,
            official_key: officialKey,
            capture: persistentCapture,
            additional: additionalUrls,
            candidates: (draft.officialImages || []).map((item) => item?.url || item).filter(Boolean),
          },
          officialImageKey: officialKey,
          officialImageMeta: {
            ...(draft.officialImageMeta || {}),
            url: officialUrl || draft.officialImageMeta?.url,
            key: officialKey,
          },
          capture: {
            original_uploaded_on_confirmation: Boolean(persistentCapture),
            additional_kinds: additionalPhotos.map((photo) => photo.kind),
            observed_at: observedAt,
            url: persistentCapture,
          },
          identification: {
            source: draft.source,
            confidence: draft.confidence,
            generation: draft.generation,
            color: draft.color,
            mode: draft.source === 'vision' && draft.confidence < 0.45
              ? 'visual_description'
              : 'identified_product',
            candidates_count: Math.min(3, draft.alternatives.length),
          },
        }),
      }
      return currency === 'EUR' ? raw : applyConversionToPayload(raw, conversion)
    }

    const queueOffline = async (reason) => {
      if (!onQueueOffline) throw reason
      await onQueueOffline({
        payload: createPayload(),
        originalPhoto: photoBlob,
        additionalPhotos,
        conversionPending: currency === 'JPY' && !convertedPrice,
        clientMutationId,
      })
    }

    try {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        await queueOffline(new Error('Réseau indisponible.'))
        return
      }
      const alreadyPersisted = persistImageUrl(draft.captureImageUrl)
      const uploaded = alreadyPersisted
        ? { url: alreadyPersisted, key: draft.captureImageKey || '' }
        : (photoBlob ? await productService.uploadScanImage(photoBlob) : {})
      const captureUrl = persistImageUrl(uploaded.url)
      if (photoBlob && !captureUrl) {
        throw new Error('Le stockage n’a pas confirmé la photographie.')
      }
      const uploadedAdditional = await Promise.all(additionalPhotos.map((photo, index) => (
        productService.uploadScanImage(photo.blob, `scan-${photo.kind}-${index + 1}.jpg`)
      )))
      onConfirm(createPayload({
        captureUrl,
        captureKey: uploaded.key || '',
        additionalUrls: uploadedAdditional.map((item) => persistImageUrl(item.url)).filter(Boolean),
      }))
    } catch (error) {
      if ((typeof navigator !== 'undefined' && navigator.onLine === false) || error instanceof TypeError) {
        try {
          await queueOffline(error)
          return
        } catch (queueError) {
          submittedRef.current = false
          setAnalyzeError(queueError.message || 'Mise en attente hors ligne impossible.')
          return
        }
      }
      submittedRef.current = false
      const raw = error?.response?.data?.detail || error?.message || ''
      if (/503|status code 503/i.test(String(raw))) {
        setAnalyzeError('Enregistrement impossible')
        return
      }
      setAnalyzeError(raw || 'Enregistrement impossible')
    }
  }

  const close = () => {
    if (step === 'review' && (userEditedRef.current || String(draft.price || '').trim())) {
      if (typeof window !== 'undefined' && !window.confirm('Abandonner la validation de ce produit ?')) return
    }
    if (step === 'analyze') {
      abortAnalyze()
      pendingAnalyzeReset.current = true
    }
    onClose()
  }

  const switchToManual = () => {
    abortAnalyze()
    reset()
    onClose()
    onManual?.()
  }

  return (
    <GlassOverlay
      isOpen={isOpen}
      onClose={close}
      allowClose={!submitting}
      returnFocusRef={returnFocusRef}
      labelledBy="scan-product-title"
      panelClassName={step === 'analyze' ? 'scan-analyze-sheet' : 'scan-product-dialog'}
      followPointer={false}
    >
      {step === 'analyze' ? (
        <ScanAnalyzeSheet
          statusText={statusText}
          phase={analyzePhase}
          previewUrl={draft.previewUrl}
          onClose={close}
          onManual={switchToManual}
        />
      ) : (
      <div className="scan-product-flow">
        <header className="scan-product-header">
          <div>
            <h2 id="scan-product-title">Ajouter par photo</h2>
            <p>
              {step === 'capture' && 'Placez le produit ou son code-barres dans le cadre.'}
              {step === 'review' && 'Vérifiez les informations avant d’enregistrer.'}
            </p>
          </div>
          <button type="button" className="scan-icon-btn" onClick={close} aria-label="Fermer">
            <span aria-hidden="true">✕</span>
          </button>
        </header>
        {step === 'capture' ? (
          <ScanCapture
            onCapture={handleCapture}
            onCancel={captureKind === 'product' ? close : () => {
              setCaptureKind('product')
              setStep('review')
            }}
            onManual={() => {
              switchToManual()
            }}
            captureKind={captureKind}
          />
        ) : null}
        {step === 'review' ? (
          <ScanReview
            draft={draft}
            onChange={patchDraft}
            onRetake={reset}
            onSubmit={submit}
            submitting={submitting}
            error={analyzeError || submitError}
            storeState={{
              ...store,
              onAskLocation: () => askLocation(),
              onSearch: () => { void searchPlaces(store.query) },
              onQueryChange: (query) => setStore((prev) => ({ ...prev, query })),
              onSelect: (place) => setStore((prev) => ({
                ...prev,
                selected: place,
                storeName: place.name,
                storeAddress: place.address,
                status: 'ready',
                mode: 'confirmed',
                places: [],
              })),
              onNameChange: (storeName) => setStore((prev) => ({
                ...prev,
                storeName,
                mode: prev.selected ? 'confirmed' : (prev.status === 'coords-only' || prev.coords ? 'coords-only' : 'manual'),
              })),
              onAddressChange: (storeAddress) => setStore((prev) => ({
                ...prev,
                storeAddress,
                mode: prev.selected ? 'confirmed' : (prev.status === 'coords-only' || prev.coords ? 'coords-only' : 'manual'),
              })),
              onSavePositionOnly: () => { void savePositionOnly() },
              onEditAssociation: () => setStore((prev) => ({
                ...prev,
                selected: null,
                status: prev.coords ? 'ready' : 'idle',
                mode: '',
              })),
            }}
            onAdditionalPhoto={(kind) => {
              setCaptureKind(kind)
              setStep('capture')
            }}
            onRejectSuggestion={(candidate) => {
              const rejectedName = candidate.name_fr || candidate.name || candidate.suggestedName
              setDraft((prev) => ({
                ...prev,
                alternatives: prev.alternatives.filter((item) => {
                  const itemName = typeof item === 'string'
                    ? item
                    : item.name_fr || item.name || item.suggestedName
                  return itemName !== rejectedName
                }),
              }))
            }}
            onCropChange={(productBox) => setDraft((prev) => ({ ...prev, productBox }))}
            onRecrop={handleRecrop}
            recroping={recroping}
            onSearchOfficialImage={searchOfficialImage}
            onOfficialDisplayError={handleOfficialDisplayError}
            onImportOfficialImage={importOfficialImage}
            onOfficialImageUrl={assignOfficialImageUrl}
            onUseCaptureAsOfficial={() => setDraft((prev) => {
              const captureUrl = persistImageUrl(prev.captureImageUrl)
              return {
                ...prev,
                officialImage: captureUrl || '',
                officialImageMeta: captureUrl ? {
                  url: captureUrl,
                  source_name: 'capture',
                } : null,
                imageSearchError: captureUrl ? '' : 'not_found',
              }
            })}
            searchingOfficial={searchingOfficial}
          />
        ) : null}
      </div>
      )}
    </GlassOverlay>
  )
}
